import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { clearCache, useRequest } from 'ahooks';
import { UserContext } from '../../contexts/UserContext';

const PDF_CACHE_TIME = 5 * 60 * 1000;

const CachedPDF: React.FC<{
  pvaId: string;
  downloadUrl: string;
  onUrlExpired: () => void;
}> = ({ pvaId, downloadUrl, onUrlExpired }) => {
  const { data: pdf, loading, error } = useRequest(
    async () => {
      const response = await axios.get(downloadUrl, { responseType: 'blob' });
      return new Blob([response.data], { type: 'application/pdf' });
    },
    {
      cacheKey: downloadUrl,
      cacheTime: PDF_CACHE_TIME,
      staleTime: PDF_CACHE_TIME,
      onError: (failure) => {
        const status = (failure as any)?.response?.status;
        if ([401, 403, 404].includes(status)) onUrlExpired();
      },
    }
  );
  const [pdfUrl, setPdfUrl] = useState<string>();
  useEffect(() => {
    if (!pdf) return;
    const objectUrl = URL.createObjectURL(pdf);
    setPdfUrl(`${objectUrl}#toolbar=0&view=Fit`);
    return () => URL.revokeObjectURL(objectUrl);
  }, [pdf]);

  return (
    <Spin spinning={loading} tip="Loading PDF...">
      {error ? (
        <Alert type="error" showIcon message="Unable to load this SVA’s PDF." />
      ) : pdfUrl ? (
        <iframe
          title={`SVA PDF preview for ${pvaId}`}
          src={pdfUrl}
          style={{
            width: '100%',
            height: 700,
            border: '1px solid #d9d9d9',
            borderRadius: 4,
          }}
        />
      ) : (
        <div style={{ minHeight: 180 }} />
      )}
    </Spin>
  );
};

const ClientSVAPDFPreview: React.FC<{ pvaId: string }> = ({ pvaId }) => {
  const { user } = useContext(UserContext);
  const urlCacheKey = user?.user_id
    ? JSON.stringify(['client-sva-pdf-url', user.user_id, user.role, pvaId])
    : undefined;
  const refreshedExpiredUrl = useRef(false);
  useEffect(() => {
    refreshedExpiredUrl.current = false;
  }, [urlCacheKey, pvaId]);
  const { data: downloadUrl, loading, error, refresh } = useRequest(
    async () => {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken();
      const response = await axios.get(
        `${process.env.REACT_APP_CHALICE_URL}/get_client_sva_pdf`,
        {
          params: { pva_id: pvaId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.data.download_url) throw new Error('No PDF URL');
      return response.data.download_url as string;
    },
    {
      cacheKey: urlCacheKey,
      cacheTime: PDF_CACHE_TIME,
      staleTime: PDF_CACHE_TIME,
      refreshDeps: [pvaId, urlCacheKey],
      onError: () => {},
    }
  );
  const status = (error as any)?.response?.status;
  return (
    <Spin spinning={loading} tip="Loading PDF...">
      {error ? (
        <Alert
          type="error"
          showIcon
          message={
            status === 403
              ? 'This SVA is no longer available to your organization.'
              : status === 400
              ? 'No PDF is available for this SVA.'
              : 'Unable to load this SVA’s PDF.'
          }
        />
      ) : !loading && downloadUrl ? (
        <CachedPDF
          key={downloadUrl}
          pvaId={pvaId}
          downloadUrl={downloadUrl}
          onUrlExpired={() => {
            // Bypass the URL cache once if OneDrive rejects an expired link.
            if (!refreshedExpiredUrl.current) {
              refreshedExpiredUrl.current = true;
              if (urlCacheKey) clearCache(urlCacheKey);
              refresh();
            }
          }}
        />
      ) : (
        <div style={{ minHeight: 180 }} />
      )}
    </Spin>
  );
};

export default ClientSVAPDFPreview;
